import React from "react";
import {useCsContext} from "../../state/CsInstance";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";

import styles from "./Editor.module.scss";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faXmark} from "@fortawesome/sharp-solid-svg-icons/faXmark";
import {faEdit} from "@fortawesome/sharp-regular-svg-icons/faEdit";
import {faPenLine} from "@fortawesome/sharp-solid-svg-icons/faPenLine";
import {observer} from "mobx-react-lite";
import ChangeFilenameModalContent from "../Modals/ChangeFilenameModalContent";

type Props = {
	/** Unique identifier for this file tab (usually its filename). */
	filename: string;
};

/**
 * Renders a tab for a file.
 */
const FileTab: React.FC<Props> = (props: Props) => {
	const {filename} = props;
	const {codeStore, globalModalStore} = useCsContext();

	// Only show tab if the file is visible
	const currentFileInfo = codeStore.visibleFileMap[filename];
	if (!currentFileInfo) {
		return null;
	}

	function removeFileOpen(fileName: string) {
		const filesOpenCopy = [...codeStore.filesOpen];
		const idx = codeStore.filesOpen.indexOf(fileName);
		filesOpenCopy.splice(idx, 1);
		codeStore.filesOpen = filesOpenCopy;

		// If all tabs are closed, set focused file to null
		if (filesOpenCopy.length === 0) {
			codeStore.focusedFile = null;
			codeStore.codeMirrorStore.showEmptyEditorPrompt();
		} else if (codeStore.focusedFile === fileName) {
			let fileToFocus;
			if (idx - 1 < 0) {
				// If the focused file was the first tab, set focused file to the new first tab
				fileToFocus = codeStore.visibleFileMap[filesOpenCopy[0]!];
			} else {
				// Otherwise, set focused file to the tab to the left of the closed tab
				fileToFocus = codeStore.visibleFileMap[filesOpenCopy[idx - 1]!];
			}
			if (fileToFocus) {
				codeStore.focusFile(fileToFocus);
			}
		}
	}

	const onDoubleClickFilename = () => {
		if (!currentFileInfo.attributes?.isOriginal) {
			globalModalStore.showModal(
				<ChangeFilenameModalContent fileData={currentFileInfo} />
			);
		}
	};

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<span
				className={[
					styles.tabButtonWrapper,
					codeStore.focusedFile === currentFileInfo.name
						? styles.tabButtonWrapperFocused
						: "",
				].join(" ")}
				onClick={() => {
					codeStore.focusFile(currentFileInfo);
				}}
			>
				<button className={styles.tabButtonContent}>
					<FontAwesomeIcon
						icon={currentFileInfo.attributes?.isOriginal ? faEdit : faPenLine}
						className={[styles.icon, styles.fileIcon].join(" ")}
					/>
					<div
						className={styles.tabButtonText}
						onDoubleClick={onDoubleClickFilename}
					>
						{currentFileInfo.name.split(".")[0]}
					</div>
				</button>
				<button
					className={styles.closeTabButton}
					onClick={(e) => {
						e.stopPropagation();
						removeFileOpen(currentFileInfo.name);
					}}
					aria-label={`Close Tab ${currentFileInfo.name}`}
				>
					<FontAwesomeIcon icon={faXmark} className={styles.icon} />
				</button>
			</span>
		</ErrorBoundary>
	);
};

export default observer(FileTab);
